/**
 * Dynamic Header Height Calculator - Ultra Performance Optimized
 * 
 * Berechnet die Header-Höhe minimal-invasiv und setzt sie als CSS-Variable.
 * Nutzt moderne Browser-APIs für maximale Performance.
 * 
 * Performance-Features:
 * - ResizeObserver mit Throttling
 * - IntersectionObserver (pausiert bei unsichtbarem Header)
 * - requestIdleCallback für nicht-kritische Updates
 * - Automatisches Cleanup bei Page Unload
 * - Frame-basierte Update-Kontrolle
 * 
 * CSS Performance-Tipp:
 * Füge zum Header hinzu: contain: layout; (reduziert Reflow-Scope)
 */

export function initDynamicHeaderHeight(document) {
  const header = document.getElementById('site-header');
  
  if (!header) {
    return;
  }

  let mutationObserver = null;
  let resizeObserver = null;
  let intersectionObserver = null;
  let updateScheduled = false;
  let lastHeight = 0;
  let isHeaderVisible = true; // Annahme: Header ist initial sichtbar
  let resizeThrottle = null;

  /**
   * Berechnet und setzt die Header-Höhe nur bei tatsächlicher Änderung
   */
  function updateHeaderHeight() {
    // Skip Update wenn Header nicht sichtbar (Intersection Observer)
    if (!isHeaderVisible) return;
    
    // Verhindere redundante Updates im selben Frame
    if (updateScheduled) return;
    
    updateScheduled = true;
    
    requestAnimationFrame(() => {
      try {
        const headerHeight = Math.round(header.getBoundingClientRect().height);
        
        // Update nur bei Änderung > 0.5px (verhindert Subpixel-Flackern)
        if (Math.abs(headerHeight - lastHeight) > 0.5) {
          lastHeight = headerHeight;
          const newValue = `${headerHeight}px`;
          
          document.documentElement.style.setProperty('--header-height-dynamic', newValue);
          
          // Debugging nur auf localhost
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.log('✓ Header height updated:', newValue, `(Δ ${Math.abs(headerHeight - lastHeight).toFixed(1)}px)`);
          }
        }
      } catch (error) {
        // Fehlerbehandlung: Header nicht mehr im DOM oder andere Fehler
        console.error('Header height calculation failed:', error);
        cleanup(); // Cleanup bei Fehler
      } finally {
        updateScheduled = false;
      }
    });
  }

  /**
   * Debounced Update mit Option für sofortige Ausführung
   */
  let debounceTimer = null;
  function debouncedUpdate(immediate = false) {
    if (immediate) {
      clearTimeout(debounceTimer);
      updateHeaderHeight();
      return;
    }
    
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateHeaderHeight, 150);
  }

  /**
   * IntersectionObserver: Pausiert Updates wenn Header nicht sichtbar
   * Spart CPU wenn User weit nach unten gescrollt hat
   */
  function startIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      return; // Nicht verfügbar - Header bleibt als "visible" behandelt
    }

    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const wasVisible = isHeaderVisible;
        isHeaderVisible = entry.isIntersecting;
        
        // Wenn Header wieder sichtbar wird, Update triggern
        if (!wasVisible && isHeaderVisible) {
          debouncedUpdate(true);
        }
      });
    }, {
      threshold: 0, // Trigger sobald auch nur 1px sichtbar ist
      rootMargin: '50px' // 50px Buffer für smooth transitions
    });

    try {
      intersectionObserver.observe(header);
    } catch (e) {
      console.warn('IntersectionObserver failed:', e);
    }
  }

  /**
   * Startet MutationObserver (nur für class-Änderungen am Header)
   */
  function startMutationObserver() {
    if (mutationObserver) return;

    mutationObserver = new MutationObserver((mutations) => {
      // Prüfe ob Mutation tatsächlich relevant ist
      const hasRelevantChange = mutations.some(mutation => {
        // Nur class-Änderungen (z.B. .is-header-visible) beeinflussen die Höhe
        return mutation.type === 'attributes' && mutation.attributeName === 'class';
      });

      if (hasRelevantChange) {
        debouncedUpdate();
      }
    });

    mutationObserver.observe(header, {
      attributes: true,
      attributeFilter: ['class'], // NUR class-Änderungen beobachten
      childList: false,           // Keine DOM-Änderungen
      subtree: false,             // Keine Kind-Elemente
      characterData: false        // Keine Text-Änderungen
    });
  }

  /**
   * Startet ResizeObserver mit Throttling (effizienteste Methode)
   */
  function startResizeObserver() {
    if (!('ResizeObserver' in window)) {
      return false; // Nicht verfügbar
    }

    resizeObserver = new ResizeObserver((entries) => {
      // Throttle: Max 1 Update pro 16ms (entspricht ~60fps)
      if (resizeThrottle) return;
      
      resizeThrottle = requestAnimationFrame(() => {
        updateHeaderHeight();
        resizeThrottle = null;
      });
    });
    
    try {
      resizeObserver.observe(header, { box: 'border-box' });
      return true; // Erfolgreich gestartet
    } catch (e) {
      console.warn('ResizeObserver failed:', e);
      return false;
    }
  }

  /**
   * Fallback auf window.resize (nur wenn ResizeObserver nicht verfügbar)
   */
  function setupResizeFallback() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateHeaderHeight, 200);
    }, { passive: true });
  }

  /**
   * Cleanup-Funktion für alle Observer und Timer
   */
  function cleanup() {
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
    if (resizeThrottle) {
      cancelAnimationFrame(resizeThrottle);
      resizeThrottle = null;
    }
    clearTimeout(debounceTimer);
  }

  // Automatisches Cleanup bei Page Unload (verhindert Memory Leaks)
  window.addEventListener('beforeunload', cleanup, { once: true });

  // === INITIALIZATION SEQUENCE ===

  // Phase 1: Synchrone Erstmessung ohne rAF – verhindert CLS durch Frame-Verzögerung.
  // Das Script läuft im Footer, der Header ist zu diesem Zeitpunkt bereits im DOM.
  const initialHeight = Math.round(header.getBoundingClientRect().height);
  if (initialHeight > 0) {
    lastHeight = initialHeight;
    document.documentElement.style.setProperty('--header-height-dynamic', `${initialHeight}px`);
  }

  // Phase 2: IntersectionObserver starten (so früh wie möglich)
  startIntersectionObserver();

  // Phase 3: Nach Font-Load Observer starten (mit requestIdleCallback für bessere Performance)
  const startObservers = () => {
    startMutationObserver();
    
    // ResizeObserver ODER window.resize (nicht beides!)
    const hasResizeObserver = startResizeObserver();
    if (!hasResizeObserver) {
      setupResizeFallback();
    }
  };

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready
      .then(() => {
        // Fonts geladen - finale Höhe berechnen
        // Nutze requestIdleCallback wenn verfügbar (verzögert Update bis Browser idle)
        const scheduleUpdate = () => debouncedUpdate(true);
        
        if ('requestIdleCallback' in window) {
          requestIdleCallback(scheduleUpdate, { timeout: 1000 });
        } else {
          scheduleUpdate();
        }
        
        // Observer sofort starten (nicht idle-abhängig)
        startObservers();
      })
      .catch(() => {
        // Fallback wenn Font API fehlschlägt
        startObservers();
      });
  } else {
    // Kein Font API - sofort Observer starten (ältere Browser)
    requestAnimationFrame(startObservers);
  }

  // Expose Cleanup für externe Verwendung (z.B. SPA-Navigation)
  if (!window.__rocketTheme) {
    window.__rocketTheme = {};
  }
  window.__rocketTheme.cleanupHeaderObserver = cleanup;
}
